<script lang="ts" setup>
/**
 * BaseTreeGrid
 *
 * 기존 ev-tree-grid를 사용하던 컴포넌트를 ModernGrid 기반의 BaseGridAdapter로 교체.
 * Props/Emit 인터페이스는 동일하게 유지하여 기존 사용처에서 변경 없이 동작합니다.
 *
 * isTreeMode=true로 설정되어 children 키를 통한 트리 구조를 지원합니다.
 */
import { useTemplateRef, useSlots, computed, type ComponentPublicInstance } from 'vue';
import BaseGridAdapter from '@/common/components/molecules/baseGridAdapter/BaseGridAdapter.vue';
import { type Emit, type Props } from '@/common/components/molecules/grid/baseTreeGrid.setup';
import { treeGridOption as defaultTreeGridOption } from '@/common/utils/define';
import type { ModernGridExposed } from '../modernGrid/modernGrid.types';

// predefined 슬롯 목록 (명시적으로 처리되는 슬롯)
const PREDEFINED_SLOTS = new Set([
  'toolbar',
  'toolbarPrefix',
  'toolbarPostfix',
  'searchSide',
  'subToolbar',
  'topToolbar',
  'customHeader',
  'tooltip-content',
]);

const slots = useSlots();

// custom cell 슬롯만 필터링 (predefined 슬롯 제외)
const customCellSlotNames = computed(() =>
  Object.keys(slots).filter((name) => !PREDEFINED_SLOTS.has(name)),
);

const props = withDefaults(defineProps<Props>(), {
  height: undefined,
  selectedRows: () => [],
  checkedRows: () => [],
  uncheckable: () => [],
  searchWord: undefined,
  columns: () => [],
  rows: () => [],
  option: () => structuredClone(defaultTreeGridOption),
  searchBarOption: () => ({
    use: false,
    mode: 'full',
    placeholder: '',
    filterSearch: {
      use: false,
    },
  }),
  exportExcelOption: () => ({}),
  hasDynamicColumns: false,
});

const emit = defineEmits<Emit>();

// BaseGridAdapter ref
const adapterRef = useTemplateRef<ComponentPublicInstance & ModernGridExposed>('adapterRef');

// expose 함수들 포워딩
defineExpose({
  forceResetEvGrid: () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (adapterRef.value as any)?.forceResetEvGrid?.();
  },
  get table() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (adapterRef.value as any)?.table;
  },
  syncGridState: () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (adapterRef.value as any)?.syncGridState?.();
  },
  exportExcel: () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (adapterRef.value as any)?.exportExcel?.();
  },
});
</script>

<template>
  <base-grid-adapter
    ref="adapterRef"
    :columns="props.columns"
    :rows="props.rows"
    :column-env-key="props.columnEnvKey"
    :selected-rows="props.selectedRows"
    :checked-rows="props.checkedRows"
    :uncheckable="props.uncheckable"
    :search-word="props.searchWord"
    :option="props.option"
    :dynamic-styles="props.dynamicStyles"
    :search-bar-option="props.searchBarOption"
    :export-excel-option="props.exportExcelOption"
    :has-dynamic-columns="props.hasDynamicColumns"
    :ref-obj="props.refObj"
    :inner-class="props.innerClass"
    :expanded="props.expanded"
    is-tree-mode
    @click-cell="(v) => emit('click-cell', v)"
    @click-cell-chip-more="(v) => emit('click-cell-chip-more', v)"
    @on-search="(v) => emit('on-search', v)"
    @page-change="(v) => emit('page-change', v)"
    @export-to-excel-from-server="(v) => emit('export-to-excel-from-server', v)"
    @update:selected-rows="(v) => emit('update:selected-rows', v)"
    @update:checked-rows="(v) => emit('update:checked-rows', v)"
    @update:search-word="(v) => emit('update:search-word', v)"
    @update:columns="(v) => emit('update:columns', v)"
    @update:ref-obj="(v) => emit('update:ref-obj', v)"
    @update:expanded="(v) => emit('update:expanded', v)"
    @check-row="(isCheck, index, data) => emit('check-row', isCheck, index, data)"
    @check-all="(isCheck, checkedDataList) => emit('check-all', isCheck, checkedDataList)"
    @click-row="(v) => emit('click-row', v)"
    @dblclick-row="(v) => emit('dblclick-row', v)"
    @sort-column="(v) => emit('sortColumn', v)"
    @change-column-info="(v) => emit('change-column-info', v)"
  >
    <!-- toolbar 슬롯 포워딩 -->
    <template
      v-if="$slots.toolbar"
      #toolbar
    >
      <slot name="toolbar" />
    </template>

    <!-- toolbarPrefix 슬롯 포워딩 -->
    <template
      v-if="$slots.toolbarPrefix"
      #toolbarPrefix
    >
      <slot name="toolbarPrefix" />
    </template>

    <!-- toolbarPostfix 또는 searchSide 슬롯 포워딩 -->
    <template
      v-if="$slots.toolbarPostfix || $slots.searchSide"
      #toolbarPostfix
    >
      <!-- toolbarPostfix는 그대로, searchSide는 div로 감싸기 -->
      <slot name="toolbarPostfix" />
      <div
        v-if="$slots.searchSide"
        class="grid-toolbar__search-side"
      >
        <slot name="searchSide" />
      </div>
    </template>

    <!-- subToolbar 슬롯 포워딩 -->
    <template
      v-if="$slots.subToolbar"
      #subToolbar
    >
      <slot name="subToolbar" />
    </template>

    <!-- topToolbar 슬롯 포워딩 -->
    <template
      v-if="$slots.topToolbar"
      #topToolbar
    >
      <slot name="topToolbar" />
    </template>

    <!-- customHeader 슬롯 포워딩 -->
    <template
      v-if="$slots.customHeader"
      #customHeader
    >
      <slot name="customHeader" />
    </template>

    <!-- tooltip-content 슬롯 포워딩 -->
    <template
      v-if="$slots['tooltip-content']"
      #tooltip-content
    >
      <slot name="tooltip-content" />
    </template>

    <!-- 동적 커스텀 셀 슬롯 포워딩 (predefined 슬롯 제외) -->
    <template
      v-for="slotName in customCellSlotNames"
      :key="slotName"
      #[slotName]="slotProps"
    >
      <slot
        :name="slotName"
        v-bind="slotProps || {}"
      />
    </template>
  </base-grid-adapter>
</template>
